import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText, Printer, Search, Loader2, Plus, Pencil, Check, X,
  Building2, User, Users, Briefcase, Eye, EyeOff, Trash2, Link2, Copy,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  PRINT_CSS, fmtDate,
  buildFleetHtml, buildRecruiterHtml, buildSetterHtml,
} from "./agreementBuilders";

const ACCENT = "#FF4D00";
const GOLD   = "#F5C842";
const DARK   = "#0A0A0A";
const CARD   = "#111113";

// ─── Helpers ─────────────────────────────────────────────────────────────
// PRINT_CSS, fmtDate, buildFleetHtml, buildRecruiterHtml, buildSetterHtml
// are imported from ./agreementBuilders

// ─── HTML builders (carrier + driver remain local) ────────────────────────
function buildCarrierHtml(a) {
  const sigDate = fmtDate(a.signed_at);
  return `
    <div class="header">
      <div>
        <div class="logo">Andrej Sasaroga DBA Hylas Companion</div>
        <h1>Fleet Client Agreement <span class="tag">Signed</span></h1>
        <div class="meta">Effective: ${sigDate} · Confidential</div>
      </div>
      <div style="text-align:right" class="meta">${a.carrier_name}<br/>${a.email !== "—" ? a.email : ""}</div>
    </div>
    <h2>1. Parties</h2>
    <p>This Fleet Client Agreement ("Agreement") is between <strong>Andrej Sasaroga DBA Hylas Companion</strong> ("Hylas"), contact@hylascompanion.com, and <strong>${a.carrier_name}</strong> ("Client"), Email ${a.email !== "—" ? a.email : "[______]"}. Hylas is a driver recruiting and marketing service only.</p>
    <h2>2. Services — Lead Generation Only</h2>
    <p>Hylas provides CDL driver sourcing, marketing, and initial non-regulated pre-screening based on driver self-reported data. Hylas markets Client's open positions and refers candidates.</p>
    <p>Hylas does not: verify CDL, obtain MVR, PSP, Clearinghouse, background checks, DOT compliance; act as motor carrier, employer, C/TPA, or consumer reporting agency; guarantee any hire. All qualification per 49 CFR 391, safety review, drug testing, Clearinghouse queries, and hiring decisions are the sole responsibility of Client.</p>
    <h2>3. Fee Schedule — Variable Placement Fee</h2>
    <p style="font-size:10px;color:#888;margin:0 0 8px">(Live at <a href="https://hylascompanion.com/market/pricing">hylascompanion.com/market/pricing</a> · Floor Prices Effective ${new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })})</p>
    <p><strong>a) Base Floor Prices — No Maximum Cap, Bid Higher to Win</strong></p>
    <p>Placement Fee to Hylas is variable and set by live auction. Client's winning bid is the final fee. Minimum floors:</p>
    <table>
      <tr><th colspan="3"><strong>SEMI TRUCK ($1,500–$2,100+ Floor)</strong></th></tr>
      <tr><td>Company Driver</td><td>Floor $1,500</td><td style="color:#888;font-size:10px">Min Driver Pay $0.70 CPM</td></tr>
      <tr><td>Rental</td><td>Floor $1,700</td><td style="color:#888;font-size:10px">Min 85% Revenue</td></tr>
      <tr><td>Lease Purchase</td><td>Floor $1,900</td><td style="color:#888;font-size:10px">Min 85% Revenue</td></tr>
      <tr><td>Owner Operator</td><td><strong>Floor $2,100</strong></td><td style="color:#888;font-size:10px">Min 88% Revenue</td></tr>
    </table>
    <table style="margin-top:4px">
      <tr><th colspan="2"><strong>BOX TRUCK ($800–$1,400+ Floor)</strong></th></tr>
      <tr><td colspan="2">Floor range $800–$1,400+ depending on pay program and package. No maximum cap.</td></tr>
    </table>
    <p style="font-size:10px;color:#888;margin:4px 0 8px">System displays "4 CARRIERS ARE COMPETING FOR THIS DRIVER RIGHT NOW" as market indicator. Higher bid increases win rate. Fee locked at winning bid per email/ATS timestamp.</p>
    <p><strong>b) Payment Structure — Client elects one at checkout:</strong></p>
    <ul>
      <li><strong>i) Standard 50/50 (Default):</strong> 50% due on Driver Hire Day (First Dispatch), 50% due 14 days after First Dispatch. Replacement Credit per Section 3B applies.</li>
      <li><strong>ii) One-Time Payment:</strong> 100% due on Driver Hire Day (First Dispatch). Includes $100–$600 discount vs Standard 50/50 depending on truck type and bidding (e.g., $1,500 Standard = $1,200 One-Time). Replacement Credit per Section 3B applies. Fully earned, non-refundable.</li>
      <li><strong>iii) Hylas Weekly Flex:</strong> From $55/week (Box) / $95/week (Semi) with auto-stop. Plans: 9 Wks / 17 Wks (Popular) / 35 Wks / 52 Wks. 4-week minimum commitment. Billing cycle Mon 12:00am–Sun 11:59pm CST. If Driver leaves any day Mon–Sun, that week is non-refundable and fully earned. Client must notify Hylas within 24h of separation via email to support@hylascompanion.us; failure to notify = following week billable. Subscription auto-stops. No Replacement Credit for Weekly Flex.
        <br/><strong>Weekly Flex Floor Pricing:</strong> Semi Truck: 9 Wks $275–$375 / 17 Wks $185–$255 / 35 Wks $120–$165 / 52 Wks $95–$135. Box Truck: 9 Wks $150–$250 / 17 Wks $100–$170 / 35 Wks $65–$110 / 52 Wks $55–$90.</li>
    </ul>
    <p><strong>c) First Dispatch Definition:</strong> First load dispatched to Driver by Client OR Day 7 after orientation start date, whichever occurs first. Orientation start = first day Driver appears at Client facility or logs into ELD. Fee is fully earned upon First Dispatch.</p>
    <p><strong>d) Late Fees &amp; Remedies:</strong> $300/day per Semi Truck placement and $150/day per Box Truck placement as liquidated damages (not interest), capped at 100% of Placement Fee per driver. 1.5%/month interest after 30 days overdue on total outstanding. Hylas may suspend all new driver transmissions and pause active bids during any arrears over 7 days.</p>
    <p><strong>e) Immediate Remarketing Rights — All Payment Structures:</strong> If Client fails to pay any amount when due within 7 days of due date — including if Driver has already started driving and is 7 days late on payment — Hylas has the immediate right to return Driver to Hylas Market and re-market, transmit and place Driver with another fleet, even while Driver is still dispatched with original Client. Due dates are: (i) Standard 50/50: Day 0 and Day 14; (ii) One-Time: Day 0; (iii) Weekly Flex: each Monday per billing cycle. Failure to pay any weekly installment within 7 days triggers same rights. If Driver separates from Client for any reason at any time after First Dispatch, Hylas may immediately re-market Driver. Client remains liable for original fee plus late fees and interest, and shall not interfere with Hylas re-marketing efforts. This right is based on Driver's 12-month Application Sharing Authorization.
    <p><strong>f) Pricing Page Control:</strong> Live simulator at /market/pricing controls. In case of conflict between this Agreement and Pricing Page, winning bid confirmation (email/ATS timestamp) on transmission date controls. Floor prices may be updated with 7 days notice.</p>
    <h2>3B. Replacement Credit — First Week Net Loss</h2>
    <p>If driver quits, abandons, or is terminated for cause within 7 calendar days from first dispatch and Client is in documented net loss (expenses &gt; revenue):</p>
    <ul>
      <li>a) Client must notify Hylas within 48 hours and provide settlement sheets / expense proof showing net loss.</li>
      <li>b) Credit equals documented net loss, capped at amount actually paid to Hylas for that driver.</li>
      <li>c) Credit applies only to next driver placement fee — non-refundable, non-cash, expires after 90 days.</li>
      <li>d) Applies ONLY to Standard 50/50 and One-Time plans. Weekly Flex has no replacement credit.</li>
      <li>e) One credit per failed placement. No stacking.</li>
    </ul>
    <h2>4. 45-Day Ownership &amp; Non-Circumvention</h2>
    <div class="highlight">Driver submitted via Hylas and transmitted to Client is protected <strong>45 days</strong> from transmission date. Hiring, leasing, or using driver in any capacity within 45 days (including via affiliate, other recruiter, or as owner-op) triggers full fee. Proof = Hylas email/ATS timestamp.</div>
    <h2>5. Partner Marketing</h2>
    <p>Client is independent, separate from Hylas. Client authorizes Hylas to market Client as a "Partner Fleet/Carrier" for recruiting only. Client shall not represent Hylas as its employer or carrier.</p>
    <h2>6. No Referral Solicitation</h2>
    <p>Client shall not offer referral bonuses to Hylas drivers to refer other Hylas drivers during Ownership Window and first 90 days. <strong>Breach = $1,500 per solicitation.</strong></p>
    <h2>7. Client Responsibilities</h2>
    <p>Client acknowledges Hylas does not obtain consumer reports. Client is solely responsible for FCRA compliance, FMCSA Clearinghouse queries, MVR, PSP, 49 CFR 391.23 checks, and DOT compliance. Client represents it has valid authority and insurance to hire drivers, directly or under a partner carrier's authority.</p>
    <h2>8. Liability Transfer &amp; Indemnification</h2>
    <p>Upon first dispatch, all employer liability transfers to Client — including pay, taxes, workers' comp, equipment, insurance, DOT/FMCSA, ELD, and DOL. Hylas bears zero employer liability after placement. Client shall indemnify, defend, and hold harmless Hylas from any claims, DOT audits, fines, accidents, wage claims, or lawsuits arising from the driver's employment or operation under Client.</p>
    <h2>9. Data &amp; Confidentiality</h2>
    <p>Client acknowledges Hylas has driver authorization to transmit applications per the Multi-Carrier Lead Authorization. Client shall use data only for employment evaluation, store securely, and shall not resell. Fees and terms are confidential.</p>
    <h2>10. Default</h2>
    <p>If payment is not received within 7 days after written email notice of default, all outstanding fees become immediately due and Hylas may pursue collection and legal remedies including attorney fees.</p>
    <h2>11. General Terms</h2>
    <ul>
      <li><strong>a) Term:</strong> Month-to-month, 7-day written email termination. Sections 4, 6, 7, 8, 10 survive.</li>
      <li><strong>b) No Guarantee.</strong></li>
      <li><strong>c) No Joint Employer/Partnership.</strong></li>
      <li><strong>d) Entire Agreement.</strong></li>
      <li><strong>e) Governing Law &amp; Arbitration:</strong> Binding arbitration in Cook County, Illinois under FAA rules, individual basis only. Prevailing party gets attorney fees.</li>
      <li><strong>f) E-SIGN valid.</strong></li>
    </ul>
    <div class="sig-block">
      <p style="font-size:9px;text-transform:uppercase;letter-spacing:0.1em;color:#FF4D00;font-weight:bold;margin:0 0 4px">
        Agreed via Clickwrap E-Signature — ESIGN Act (15 U.S.C. § 7001)
      </p>
      <p style="font-size:9px;color:#888;margin:0 0 16px">
        &#9745; I have read and agree to the Fleet Client Agreement &nbsp;·&nbsp; ${a.signed_ip && a.signed_ip !== "—" ? `IP: ${a.signed_ip} · ` : ""}${sigDate}
      </p>
      <table class="sig-table"><tr>
        <td style="padding-right:40px;vertical-align:top">
          <p class="sig-label">Hylas Companion</p>
          <p style="font-size:10px;color:#555;margin:0">Andrej Sasaroga DBA Hylas Companion</p>
          <p style="font-size:9px;color:#999;margin:6px 0 0">Date: ${sigDate}</p>
        </td>
        <td style="vertical-align:top">
          <p class="sig-label">Client: ${a.carrier_name}</p>
          <p style="font-size:15px;font-style:italic;font-weight:bold;margin:0;color:#1a1a1a">${a.signer_name}</p>
          <p style="font-size:9px;color:#999;margin:4px 0 0">Typed Signature &nbsp;·&nbsp; Date: ${sigDate}</p>
        </td>
      </tr></table>
    </div>`;
}

function buildDriverHtml(d) {
  const appDate = fmtDate(d.created_at);
  const fullName = `${d.first_name} ${d.last_name}`.trim();
  return `
    <div class="header">
      <div>
        <div class="logo">Hylas Companion Group</div>
        <h1>Driver Placement &amp; Consent Agreement</h1>
        <div class="meta">Application Date: ${appDate} · Confidential</div>
      </div>
      <div style="text-align:right" class="meta">CDL Class: ${d.cdl_class || "—"}<br/>State: ${d.state || "—"}</div>
    </div>
    <h2>1. Driver Information</h2>
    <table style="width:100%;border-collapse:collapse;font-size:11px">
      <tr>
        <td style="width:25%;padding:4px 8px 4px 0;color:#555">Full Name</td><td style="padding:4px 8px 4px 0"><strong>${fullName}</strong></td>
        <td style="width:25%;padding:4px 8px 4px 0;color:#555">CDL Class</td><td><strong>${d.cdl_class || "—"}</strong></td>
      </tr>
      <tr>
        <td style="padding:4px 8px 4px 0;color:#555">Email</td><td style="padding:4px 8px 4px 0">${d.email || "—"}</td>
        <td style="padding:4px 8px 4px 0;color:#555">Phone</td><td>${d.phone || "—"}</td>
      </tr>
      <tr>
        <td style="padding:4px 8px 4px 0;color:#555">State</td><td style="padding:4px 8px 4px 0">${d.state || "—"}</td>
        <td style="padding:4px 8px 4px 0;color:#555">Position</td><td>${d.position || "—"}</td>
      </tr>
    </table>
    <h2>2. Consent to Recruit &amp; Market</h2>
    <p>By submitting this application, <strong>${fullName}</strong> explicitly consents to Hylas Companion Group, LLC marketing their profile to qualified partner carriers for employment matching.</p>
    <div class="highlight">Driver acknowledges that Hylas Companion is a driver placement service — not a direct employer. Final employment is between the driver and the partner carrier.</div>
    <h2>3. TCPA Consent &amp; Electronic Communication</h2>
    <p>Driver expressly consents to receive calls, texts, and emails from Hylas Companion at the phone number and email provided. Message and data rates may apply.</p>
    ${d.tcpa_consent_text ? `<div class="highlight" style="font-size:10px;color:#444">"${d.tcpa_consent_text}"</div>` : ""}
    <p class="meta">TCPA Timestamp: ${fmtDate(d.tcpa_consent_timestamp) || appDate}</p>
    <h2>4. Background &amp; Consumer Reports Authorization (FCRA)</h2>
    <p>Driver authorizes Hylas and partner carriers to obtain consumer reports including MVR, PSP, DAC/DriveCheck, and FMCSA Clearinghouse queries in strict compliance with the FCRA.</p>
    <h2>5. Governing Law</h2>
    <p>Governed by Illinois law. Disputes resolved through binding arbitration on an individual basis.</p>
    <div class="sig-block">
      <table class="sig-table"><tr>
        <td><p class="sig-label">Hylas Companion Group, LLC</p><p class="sig-name">Andrej Sasaroga</p><p class="sig-date">Owner · ${appDate}</p></td>
        <td><p class="sig-label">CDL Driver / Applicant</p><p class="sig-name">${fullName}</p><p class="sig-date">Application Submitted · ${appDate}</p></td>
      </tr></table>
    </div>`;
}

function getHtml(type, item) {
  if (type === "carrier")   return buildCarrierHtml(item);
  if (type === "driver")    return buildDriverHtml(item);
  if (type === "recruiter") return buildRecruiterHtml(item);
  if (type === "setter")    return buildSetterHtml(item);
  if (type === "fleet")     return buildFleetHtml(item || {});
  return "";
}

function printAgreement(type, item) {
  const titles = {
    carrier:   `Fleet Client — ${item?.carrier_name || "Template"}`,
    driver:    `Driver Consent — ${item?.first_name || ""} ${item?.last_name || ""}`,
    recruiter: `Recruiter — ${item?.name || "Draft"}`,
    setter:    `Setter — ${item?.name || "Draft"}`,
    fleet:     "Fleet Client Agreement — Template",
  };
  const win = window.open("", "_blank");
  if (!win) { toast.error("Popup blocked — allow popups for this site."); return; }
  win.document.write(`<!DOCTYPE html><html><head><title>${titles[type]}</title><style>${PRINT_CSS}</style></head><body>${getHtml(type, item)}</body></html>`);
  win.document.close(); win.focus();
  setTimeout(() => win.print(), 600);
}

// ─── Inline editable field ────────────────────────────────────────────────
function Blank({ value, onChange, placeholder, wide }) {
  return (
    <input
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder || "_______________"}
      style={{
        display: "inline-block",
        width: wide ? "220px" : "160px",
        border: "none",
        borderBottom: value ? "1.5px solid #333" : "1.5px dashed #FF4D00",
        background: value ? "transparent" : "rgba(255,77,0,0.04)",
        fontFamily: "Arial, sans-serif",
        fontSize: "11px",
        fontStyle: value ? "normal" : "italic",
        color: value ? "#111" : "#FF4D00",
        padding: "1px 4px",
        outline: "none",
        cursor: "text",
      }}
    />
  );
}

// ─── Staff editable preview (React-rendered) ──────────────────────────────
function StaffEditablePreview({ role, form, onChange }) {
  const F = (key, placeholder, wide) => (
    <Blank value={form[key] || ""} onChange={v => onChange(key, v)} placeholder={placeholder} wide={wide} />
  );
  const sigDate  = form.signed_at   ? fmtDate(form.signed_at)   : null;
  const startDate = form.start_date  ? fmtDate(form.start_date)  : null;
  const isRecruiter = role === "recruiter";

  return (
    <div style={{ fontFamily: "Arial, sans-serif", fontSize: "11px", lineHeight: "1.65", color: "#1a1a1a", padding: "0" }}>
      {/* Header */}
      <div style={{ borderBottom: "3px solid #FF4D00", paddingBottom: "16px", marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
        <div>
          <div style={{ fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.18em", color: "#FF4D00", fontWeight: "bold" }}>Hylas Companion Group</div>
          <div style={{ fontSize: "20px", fontWeight: "bold", marginTop: "4px" }}>
            Independent {isRecruiter ? "Recruiter" : "Setter"} Agreement
            <span style={{ display: "inline-block", background: "#FFF3E0", color: "#FF4D00", padding: "2px 8px", fontSize: "9px", textTransform: "uppercase", letterSpacing: "0.12em", border: "1px solid #FFCC80", marginLeft: "8px" }}>
              {form.name ? "Issued" : "Draft"}
            </span>
          </div>
          <div style={{ fontSize: "9px", color: "#888", textTransform: "uppercase", letterSpacing: "0.1em", marginTop: "4px" }}>
            Effective: {startDate ? startDate : F("start_date", "MM/DD/YYYY")} · Confidential
          </div>
        </div>
      </div>

      {/* 1. Parties */}
      <SectionTitle>1. Parties</SectionTitle>
      <p style={{ margin: "0 0 6px" }}>
        This Independent {isRecruiter ? "Recruiter" : "Setter"} Agreement is entered into as of{" "}
        <strong>{sigDate || F("signed_at", "MM/DD/YYYY")}</strong> between{" "}
        <strong>Hylas Companion Group, LLC</strong> ("Company") and{" "}
        <strong>{form.name ? form.name : F("name", "Full name", true)}</strong> ({isRecruiter ? '"Recruiter"' : '"Setter"'}).
      </p>
      <p style={{ margin: "0 0 14px" }}>
        <strong>Contact:</strong>{" "}
        {form.email ? form.email : F("email", "Email address", true)}{" · "}
        {form.phone ? form.phone : F("phone", "Phone number")}
      </p>

      {/* 2. Scope */}
      <SectionTitle>2. Scope of Work</SectionTitle>
      {isRecruiter ? (
        <ul style={{ margin: "4px 0 12px", paddingLeft: "16px" }}>
          <li style={{ margin: "3px 0" }}>Source and pre-screen qualified CDL drivers (Class A &amp; B) actively seeking placement.</li>
          <li style={{ margin: "3px 0" }}>Submit driver profiles through designated Hylas channels.</li>
          <li style={{ margin: "3px 0" }}>Maintain communication with sourced drivers through the pre-placement funnel.</li>
          <li style={{ margin: "3px 0" }}>Ensure all drivers have provided TCPA-compliant consent.</li>
        </ul>
      ) : (
        <ul style={{ margin: "4px 0 12px", paddingLeft: "16px" }}>
          <li style={{ margin: "3px 0" }}>Contact motor carriers to introduce the Hylas Companion driver placement platform.</li>
          <li style={{ margin: "3px 0" }}>Qualify leads per Company criteria (minimum fleet size, active hiring need, DOT authority).</li>
          <li style={{ margin: "3px 0" }}>Schedule qualified discovery calls/demos between prospects and the Hylas sales team.</li>
          <li style={{ margin: "3px 0" }}>Log all activity in the Company-provided CRM system.</li>
        </ul>
      )}

      {/* 3. Compensation */}
      <SectionTitle>3. Compensation — 3 Payment Plans</SectionTitle>
      <p style={{ margin: "0 0 8px", fontSize: "11px" }}>Paid <strong>per successfully placed driver</strong>. Rate depends on lead source and carrier payment plan. Three active plans on Hylas Market:</p>

      {/* Plan A: Standard */}
      <div style={{ background: "#111", padding: "5px 10px", margin: "10px 0 0", display: "flex", alignItems: "center", gap: "8px" }}>
        <span style={{ fontSize: "8px", fontWeight: 900, letterSpacing: "0.18em", textTransform: "uppercase", color: "#fff" }}>Plan A — Standard 50/50</span>
        <span style={{ fontSize: "9px", color: "rgba(255,255,255,0.5)", marginLeft: "auto" }}>{isRecruiter ? "Our Leads 50% · Your Leads 75%" : "Full payout over 2 milestones"}</span>
      </div>
      {isRecruiter ? (
        <table style={{ width: "100%", borderCollapse: "collapse", margin: "0 0 4px", fontSize: "10px" }}>
          <thead><tr>
            <th style={{ background: "#111", color: "#fff", padding: "5px 8px", textAlign: "left", fontSize: "8px", textTransform: "uppercase", letterSpacing: "0.12em" }}>Equipment</th>
            <th style={{ background: "#111", color: "#fff", padding: "5px 8px", textAlign: "left", fontSize: "8px", textTransform: "uppercase", letterSpacing: "0.12em" }}>Program</th>
            <th style={{ background: "#1C1C1E", color: "#fff", padding: "5px 8px", textAlign: "left", fontSize: "8px", textTransform: "uppercase", letterSpacing: "0.12em" }}>Our Leads 50%</th>
            <th style={{ background: "#FF4D00", color: "#fff", padding: "5px 8px", textAlign: "left", fontSize: "8px", textTransform: "uppercase", letterSpacing: "0.12em" }}>Your Leads 75%</th>
          </tr></thead>
          <tbody>
            {[
              ["Dry Van","Company Driver","$600","$900"],["Dry Van","Rental","$700","$1,050"],
              ["Dry Van","Lease-to-Own","$800","$1,200"],["Dry Van","Owner-Operator","$900","$1,350"],
              ["Reefer","Company Driver","$700","$1,050"],["Reefer","Rental","$800","$1,200"],
              ["Reefer","Lease-to-Own","$900","$1,350"],["Reefer","Owner-Operator","$1,000","$1,500"],
              ["Flatbed","Company Driver","$800","$1,200"],["Flatbed","Rental","$900","$1,350"],
              ["Flatbed","Lease-to-Own","$1,000","$1,500"],["Flatbed TOP ✦","Owner-Operator","$1,100 ✦","$1,650 ✦"],
            ].map(([eq, prog, our, your_], i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fafaf8" }}>
                <td style={{ padding: "4px 8px", borderBottom: "1px solid #eee", fontSize: "10px" }}><strong>{eq}</strong></td>
                <td style={{ padding: "4px 8px", borderBottom: "1px solid #eee", fontSize: "10px" }}>{prog}</td>
                <td style={{ padding: "4px 8px", borderBottom: "1px solid #eee", fontWeight: 800, color: "#FF4D00" }}>{our}</td>
                <td style={{ padding: "4px 8px", borderBottom: "1px solid #eee", fontWeight: 800, color: "#2563EB" }}>{your_}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", margin: "0 0 4px", fontSize: "10px" }}>
          <thead><tr>
            <th style={{ background: "#111", color: "#fff", padding: "5px 8px", textAlign: "left", fontSize: "8px", textTransform: "uppercase", letterSpacing: "0.12em" }}>Equipment</th>
            <th style={{ background: "#111", color: "#fff", padding: "5px 8px", textAlign: "left", fontSize: "8px", textTransform: "uppercase", letterSpacing: "0.12em" }}>Company</th>
            <th style={{ background: "#111", color: "#fff", padding: "5px 8px", textAlign: "left", fontSize: "8px", textTransform: "uppercase", letterSpacing: "0.12em" }}>Rental</th>
            <th style={{ background: "#111", color: "#fff", padding: "5px 8px", textAlign: "left", fontSize: "8px", textTransform: "uppercase", letterSpacing: "0.12em" }}>Lease</th>
            <th style={{ background: "#111", color: "#fff", padding: "5px 8px", textAlign: "left", fontSize: "8px", textTransform: "uppercase", letterSpacing: "0.12em" }}>Owner-Op</th>
          </tr></thead>
          <tbody>
            {[
              ["Dry Van","$396","$462","$528","$594"],
              ["Reefer (+$66)","$462","$528","$594","$660"],
              ["Flatbed TOP ✦","$528","$594","$660","$726 ✦"],
            ].map(([eq, a, b, c, d], i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fafaf8" }}>
                <td style={{ padding: "4px 8px", borderBottom: "1px solid #eee", fontSize: "10px" }}><strong>{eq}</strong></td>
                {[a,b,c,d].map((v,j) => <td key={j} style={{ padding: "4px 8px", borderBottom: "1px solid #eee", fontWeight: 800, color: "#FF4D00" }}>{v}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p style={{ margin: "0 0 8px", fontSize: "10px", color: "#666" }}>Payment split: 50% after driver's first load + 50% after 2 completed weeks.</p>

      {/* Plan B: One-Time */}
      <div style={{ background: "#7a5f00", padding: "5px 10px", margin: "10px 0 0", display: "flex", alignItems: "center", gap: "8px" }}>
        <span style={{ fontSize: "8px", fontWeight: 900, letterSpacing: "0.18em", textTransform: "uppercase", color: "#fff" }}>Plan B — One-Time Payment</span>
        <span style={{ fontSize: "9px", color: "rgba(255,255,255,0.5)", marginLeft: "auto" }}>Carrier pays lump-sum · Reduced {isRecruiter ? "recruiter" : "setter"} bonus</span>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", margin: "0 0 4px", fontSize: "10px" }}>
        <thead><tr>
          <th style={{ background: "#111", color: "#fff", padding: "5px 8px", textAlign: "left", fontSize: "8px", textTransform: "uppercase", letterSpacing: "0.12em" }}>Program</th>
          <th style={{ background: "#111", color: "#fff", padding: "5px 8px", textAlign: "left", fontSize: "8px", textTransform: "uppercase", letterSpacing: "0.12em" }}>Carrier Discount</th>
          <th style={{ background: "#111", color: "#fff", padding: "5px 8px", textAlign: "left", fontSize: "8px", textTransform: "uppercase", letterSpacing: "0.12em" }}>{isRecruiter ? "Recruiter" : "Setter"} Bonus</th>
          <th style={{ background: "#111", color: "#fff", padding: "5px 8px", textAlign: "left", fontSize: "8px", textTransform: "uppercase", letterSpacing: "0.12em" }}>Paid After</th>
        </tr></thead>
        <tbody>
          {(isRecruiter ? [
            ["Company Driver","$300 off","$100"],["Rental","$400 off","$150"],
            ["Lease-to-Own","$500 off","$200"],["Owner-Operator","$600 off","$250"],
            ["Flatbed Owner-Op ✦","$600 off + TOP","$300 ✦"],
          ] : [
            ["Company Driver","$300 off","$50"],["Rental","$400 off","$100"],
            ["Lease-to-Own","$500 off","$150"],["Owner-Operator","$600 off","$200"],
          ]).map(([prog, disc, bonus], i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fafaf8" }}>
              <td style={{ padding: "4px 8px", borderBottom: "1px solid #eee", fontSize: "10px" }}><strong>{prog}</strong></td>
              <td style={{ padding: "4px 8px", borderBottom: "1px solid #eee", fontSize: "10px" }}>{disc}</td>
              <td style={{ padding: "4px 8px", borderBottom: "1px solid #eee", fontWeight: 800, color: "#7a5f00" }}>{bonus}</td>
              <td style={{ padding: "4px 8px", borderBottom: "1px solid #eee", fontSize: "10px" }}>Paid immediately after Hylas receives carrier payment</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p style={{ margin: "0 0 8px", fontSize: "10px", color: "#666" }}>Paid as a single payment per hire.</p>

      {/* Plan C: Weekly Flex */}
      <div style={{ background: "#FF4D00", padding: "5px 10px", margin: "10px 0 0", display: "flex", alignItems: "center", gap: "8px" }}>
        <span style={{ fontSize: "8px", fontWeight: 900, letterSpacing: "0.18em", textTransform: "uppercase", color: "#fff" }}>Plan C — Weekly Flex</span>
        <span style={{ fontSize: "9px", color: "rgba(255,255,255,0.8)", marginLeft: "auto" }}>{isRecruiter ? "Recruiter earns 25% of carrier weekly rate — recurring" : "Setter earns 15% of carrier weekly rate — recurring"}</span>
      </div>
      <div style={{ background: "#111", color: "#fff", padding: "10px 12px", marginBottom: "0" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px" }}>
          <div>
            <p style={{ fontSize: "8px", letterSpacing: "0.15em", textTransform: "uppercase", color: "rgba(255,255,255,0.5)", margin: "0 0 2px" }}>Driver Earns</p>
            <p style={{ fontSize: "18px", fontWeight: 900, color: "#4ADE80", margin: 0 }}>10%</p>
            <p style={{ fontSize: "9px", color: "rgba(255,255,255,0.45)", margin: 0 }}>of weekly rate</p>
          </div>
          <div style={{ borderLeft: "1px solid rgba(255,255,255,0.1)", paddingLeft: "12px" }}>
            <p style={{ fontSize: "8px", letterSpacing: "0.15em", textTransform: "uppercase", color: "rgba(255,255,255,0.5)", margin: "0 0 2px" }}>Setter Earns</p>
            <p style={{ fontSize: "18px", fontWeight: 900, color: "#FFA500", margin: 0 }}>15%</p>
            <p style={{ fontSize: "9px", color: "rgba(255,255,255,0.45)", margin: 0 }}>per week, recurring</p>
          </div>
          <div style={{ borderLeft: "1px solid rgba(255,255,255,0.1)", paddingLeft: "12px" }}>
            <p style={{ fontSize: "8px", letterSpacing: "0.15em", textTransform: "uppercase", color: "rgba(255,255,255,0.5)", margin: "0 0 2px" }}>Recruiter Earns</p>
            <p style={{ fontSize: "18px", fontWeight: 900, color: "#F5C842", margin: 0 }}>25%</p>
            <p style={{ fontSize: "9px", color: "rgba(255,255,255,0.45)", margin: 0 }}>per week, recurring</p>
          </div>
        </div>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", margin: "0 0 4px", fontSize: "10px" }}>
        <thead><tr>
          <th style={{ background: "#111", color: "#fff", padding: "5px 8px", textAlign: "left", fontSize: "8px", textTransform: "uppercase", letterSpacing: "0.12em" }}>Carrier Weekly Rate</th>
          <th style={{ background: "#111", color: "#fff", padding: "5px 8px", textAlign: "left", fontSize: "8px", textTransform: "uppercase", letterSpacing: "0.12em" }}>{isRecruiter ? "Your Cut (25%/wk)" : "Your Cut (15%/wk)"}</th>
          <th style={{ background: "#111", color: "#fff", padding: "5px 8px", textAlign: "left", fontSize: "8px", textTransform: "uppercase", letterSpacing: "0.12em" }}>Max Earnings @ Plan Length</th>
        </tr></thead>
        <tbody>
          {(isRecruiter ? [
            ["$55/wk — Box Truck CD 52w","$13.75 / week","$715.00 for 52 weeks"],
            ["$100/wk — Box Truck CD 17w","$25.00 / week","$425.00 for 17 weeks"],
            ["$185/wk — Semi CD 17w","$46.25 / week","$786.25 for 17 weeks"],
            ["$255/wk — Semi OO 17w","$63.75 / week","$1,083.75 for 17 weeks"],
            ["$375/wk — Semi OO 9w","$93.75 / week","$843.75 for 9 weeks"],
          ] : [
            ["$55/wk — Box Truck CD 52w","$8.25 / week","$429.00 for 52 weeks"],
            ["$100/wk — Box Truck CD 17w","$15.00 / week","$255.00 for 17 weeks"],
            ["$185/wk — Semi CD 17w","$27.75 / week","$471.75 for 17 weeks"],
            ["$255/wk — Semi OO 17w","$38.25 / week","$650.25 for 17 weeks"],
            ["$375/wk — Semi OO 9w","$56.25 / week","$506.25 for 9 weeks"],
          ]).map(([rate, perWk, maxEarn], i) => (
            <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fafaf8" }}>
              <td style={{ padding: "4px 8px", borderBottom: "1px solid #eee", fontSize: "10px" }}>{rate}</td>
              <td style={{ padding: "4px 8px", borderBottom: "1px solid #eee", fontWeight: 800, color: "#16A34A" }}>{perWk}</td>
              <td style={{ padding: "4px 8px", borderBottom: "1px solid #eee", fontSize: "10px" }}>{maxEarn}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p style={{ margin: "0 0 8px", fontSize: "10px", color: "#666" }}>Paid every Monday for prior week. Auto-stops when Driver exits. No cap — passive income stacks with every active placement. If Driver stays beyond plan, you continue to earn weekly.</p>

      {/* Milestone Bonuses */}
      <SectionTitle>4. Milestone Bonuses</SectionTitle>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", margin: "4px 0 12px" }}>
        <div style={{ border: "1px solid #eee", padding: "10px 12px", background: "#fafaf8" }}>
          <p style={{ fontSize: "9px", fontWeight: 800, letterSpacing: "0.15em", textTransform: "uppercase", color: "#888", margin: "0 0 4px" }}>First Hire Bonus</p>
          <p style={{ fontSize: "16px", fontWeight: 900, color: "#111", margin: "0 0 4px" }}>$200</p>
          <p style={{ fontSize: "10px", color: "#666", margin: 0 }}>One-time when first driver is successfully placed (any plan).</p>
        </div>
        <div style={{ border: "1px solid #eee", padding: "10px 12px", background: "#fafaf8" }}>
          <p style={{ fontSize: "9px", fontWeight: 800, letterSpacing: "0.15em", textTransform: "uppercase", color: "#888", margin: "0 0 4px" }}>Monthly Volume Bonus</p>
          <p style={{ fontSize: "16px", fontWeight: 900, color: "#111", margin: "0 0 4px" }}>$1,000</p>
          <p style={{ fontSize: "10px", color: "#666", margin: 0 }}>6+ hired drivers in one calendar month. Paid end of month.</p>
        </div>
        <div style={{ border: "1px solid #eee", padding: "10px 12px", background: "#fafaf8" }}>
          <p style={{ fontSize: "9px", fontWeight: 800, letterSpacing: "0.15em", textTransform: "uppercase", color: "#888", margin: "0 0 4px" }}>10 Hires / Month</p>
          <p style={{ fontSize: "16px", fontWeight: 900, color: "#111", margin: "0 0 4px" }}>$2,000</p>
          <p style={{ fontSize: "10px", color: "#666", margin: 0 }}>10+ hired drivers in one calendar month. Paid end of month.</p>
        </div>
      </div>

      <div style={{ background: "#FFF8E1", borderLeft: "3px solid #FF4D00", padding: "8px 12px", margin: "8px 0 12px" }}>
        <p style={{ margin: "0 0 4px" }}>
          <strong>Start Date:</strong>{" "}
          {startDate || F("start_date", "MM/DD/YYYY")}
        </p>
        <p style={{ margin: "0 0 4px" }}>
          <strong>Custom Rate / Notes (optional):</strong>{" "}
          {form.commission ? form.commission : F("commission", "Any per-person rate override or custom notes", false)}
        </p>
        {isRecruiter && <p style={{ margin: "0", fontSize: "10px", color: "#666" }}>60-Day Tail: drivers submitted within 60 days before termination, if subsequently placed, still earn full commission.</p>}
      </div>

      {/* 5. Non-Solicitation */}
      <SectionTitle>5. Non-Solicitation &amp; Confidentiality (12 months)</SectionTitle>
      {isRecruiter ? (
        <ul style={{ margin: "4px 0 12px", paddingLeft: "16px" }}>
          <li style={{ margin: "3px 0" }}>May not recruit Hylas drivers for competing placement services.</li>
          <li style={{ margin: "3px 0" }}>May not approach Hylas partner carriers to offer competing services.</li>
          <li style={{ margin: "3px 0" }}>Must maintain strict confidentiality of driver profiles, carrier contacts, pay rates, and the Hylas Matrix.</li>
          <li style={{ margin: "3px 0" }}>Not authorized to offer referral bonuses to drivers (Hylas operates an exclusive in-house referral program).</li>
        </ul>
      ) : (
        <ul style={{ margin: "4px 0 12px", paddingLeft: "16px" }}>
          <li style={{ margin: "3px 0" }}>May not approach Hylas carrier contacts for competing services.</li>
          <li style={{ margin: "3px 0" }}>Must maintain confidentiality of carrier lists, pricing, the Hylas Matrix, and sales methodology.</li>
          <li style={{ margin: "3px 0" }}>Not authorized to offer referral bonuses to drivers (Hylas operates an exclusive in-house referral program).</li>
        </ul>
      )}

      {/* 6. Term */}
      <SectionTitle>6. Term &amp; Governing Law</SectionTitle>
      <p style={{ margin: "0 0 24px" }}>
        Commences <strong>{startDate || F("start_date", "MM/DD/YYYY")}</strong>.{" "}
        Either party may terminate with {isRecruiter ? "14" : "7"} days written notice.{" "}
        Governed by Illinois law. Binding arbitration, individual basis only.
      </p>

      {/* Signature block */}
      <div style={{ marginTop: "32px", paddingTop: "20px", borderTop: "2px solid #FF4D00" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <tbody><tr>
            <td style={{ width: "50%", paddingRight: "20px", verticalAlign: "top" }}>
              <p style={{ fontSize: "9px", textTransform: "uppercase", letterSpacing: "0.12em", color: "#777", margin: "0 0 5px" }}>Hylas Companion Group, LLC</p>
              <p style={{ fontSize: "15px", fontStyle: "italic", fontWeight: "bold", borderBottom: "1px solid #333", paddingBottom: "4px", margin: "0 0 4px" }}>Andrej Sasaroga</p>
              <p style={{ fontSize: "9px", color: "#888" }}>Owner · {sigDate || F("signed_at", "MM/DD/YYYY")}</p>
            </td>
            <td style={{ width: "50%", paddingLeft: "20px", borderLeft: "1px solid #eee", verticalAlign: "top" }}>
              <p style={{ fontSize: "9px", textTransform: "uppercase", letterSpacing: "0.12em", color: "#777", margin: "0 0 5px" }}>Independent {isRecruiter ? "Recruiter" : "Setter"}</p>
              <p style={{ fontSize: "15px", fontStyle: "italic", fontWeight: "bold", borderBottom: "1px solid #333", paddingBottom: "4px", margin: "0 0 4px" }}>
                {form.signer_name ? form.signer_name : (form.name ? form.name : F("signer_name", "Printed name"))}
              </p>
              <p style={{ fontSize: "9px", color: "#888" }}>
                {form.name || F("name", "Full name")} · {sigDate || F("signed_at", "MM/DD/YYYY")}
              </p>
            </td>
          </tr></tbody>
        </table>
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.08em", margin: "20px 0 8px", paddingBottom: "5px", borderBottom: "2px solid #FF4D00", fontWeight: "bold" }}>
      {children}
    </div>
  );
}

// ─── Preview Modal ────────────────────────────────────────────────────────
function PreviewModal({ type, item, token, onClose }) {
  const isStaff = type === "recruiter" || type === "setter";
  const [form, setForm] = useState({ ...(item ?? {}) });
  const [saving, setSaving] = useState(false);
  const iframeRef = useRef(null);

  function handleFieldChange(key, val) {
    setForm(f => ({ ...f, [key]: val }));
  }

  async function handleSaveAndPrint() {
    if (isStaff) {
      if (form.name || form.commission) {
        setSaving(true);
        try {
          await api.marketAdminSaveStaffAgreement(token, { ...form, role: type });
          toast.success("Agreement saved");
        } catch { toast.error("Save failed"); }
        finally { setSaving(false); }
      }
      printAgreement(type, form);
    } else {
      printAgreement(type, item);
    }
  }

  const htmlBody = !isStaff ? getHtml(type, item) : null;
  const fullHtml = htmlBody
    ? `<!DOCTYPE html><html><head><style>${PRINT_CSS} body{padding:24px 32px;}</style></head><body>${htmlBody}</body></html>`
    : null;

  const labelHint = isStaff && (!form.name || !form.commission)
    ? "Popuni prazna polja direktno u dokumentu — biće sačuvana pri štampanju."
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.92)" }}>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="w-full flex flex-col rounded-sm overflow-hidden"
        style={{ maxWidth: "860px", height: "90vh", background: CARD, border: "1px solid rgba(255,255,255,0.09)" }}>

        {/* Top bar */}
        <div className="flex items-center justify-between px-5 py-3 shrink-0"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.07)", background: "#0d0d0d" }}>
          <div className="flex items-center gap-3">
            <Eye className="w-4 h-4" style={{ color: ACCENT }} />
            <span className="font-head font-black text-white text-sm">Preview</span>
            {labelHint && (
              <span className="font-head text-[10px] px-2 py-0.5" style={{ color: ACCENT, background: "rgba(255,77,0,0.08)", border: "1px solid rgba(255,77,0,0.2)" }}>
                {labelHint}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleSaveAndPrint} disabled={saving}
              className="flex items-center gap-2 font-head text-[10px] uppercase tracking-[0.15em] px-4 py-2 transition-all hover:brightness-110 disabled:opacity-40"
              style={{ background: ACCENT, color: "#fff" }}>
              <Printer className="w-3.5 h-3.5" />
              {saving ? "Saving…" : isStaff && (!form.name || !form.commission) ? "Save & Print" : "Print"}
            </button>
            <button onClick={onClose} className="p-2 text-white/40 hover:text-white/80 transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Document area */}
        <div className="flex-1 overflow-y-auto" style={{ background: "#f5f5f0" }}>
          {isStaff ? (
            // React-rendered editable preview
            <div style={{ maxWidth: "720px", margin: "0 auto", background: "#fff", padding: "40px 48px", minHeight: "100%", boxShadow: "0 2px 20px rgba(0,0,0,0.15)" }}>
              <StaffEditablePreview role={type} form={form} onChange={handleFieldChange} />
            </div>
          ) : (
            // Iframe-based for signed agreements
            <iframe
              ref={iframeRef}
              srcDoc={fullHtml}
              title="Agreement Preview"
              style={{ width: "100%", height: "100%", border: "none", display: "block" }}
            />
          )}
        </div>
      </motion.div>
    </div>
  );
}

// ─── Status badge ─────────────────────────────────────────────────────────
const STATUS_COLORS = {
  approved:    { bg: "rgba(34,197,94,0.1)",   color: "#22C55E", label: "Approved" },
  live:        { bg: "rgba(245,200,66,0.1)",  color: GOLD,      label: "On Market" },
  pending:     { bg: "rgba(255,255,255,0.05)", color: "#9CA3AF", label: "Pending" },
  transferred: { bg: "rgba(99,102,241,0.1)",  color: "#818CF8", label: "Placed" },
  annulled:    { bg: "rgba(239,68,68,0.1)",   color: "#EF4444", label: "Annulled" },
};
function StatusBadge({ status }) {
  const s = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return <span className="font-head text-[9px] uppercase tracking-[0.15em] px-2 py-0.5" style={{ background: s.bg, color: s.color, border: `1px solid ${s.color}30` }}>{s.label}</span>;
}

// ─── Staff edit modal ─────────────────────────────────────────────────────
function StaffEditModal({ token, initial, role, onSaved, onClose }) {
  const [form, setForm] = useState({ id: initial?.id || "", role, name: initial?.name || "", email: initial?.email || "", phone: initial?.phone || "", start_date: initial?.start_date || "", commission: initial?.commission || "", signed_at: initial?.signed_at || "", signer_name: initial?.signer_name || "", notes: initial?.notes || "" });
  const [saving, setSaving] = useState(false);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const inputCls = "w-full px-3 py-2 text-sm text-white font-head outline-none";
  const inputStyle = { background: "#0d0d0d", border: "1px solid rgba(255,255,255,0.1)" };
  const labelCls = "block font-head text-[9px] uppercase tracking-[0.2em] mb-1.5";

  async function save() {
    setSaving(true);
    try { const res = await api.marketAdminSaveStaffAgreement(token, form); toast.success("Saved"); onSaved({ ...form, id: res.id }); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.85)" }}>
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-lg rounded-sm overflow-hidden" style={{ background: CARD, border: "1px solid rgba(255,255,255,0.08)" }}>
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: "1px solid rgba(255,255,255,0.07)", background: "#0d0d0d" }}>
          <div>
            <p className="font-head text-[9px] uppercase tracking-[0.2em]" style={{ color: ACCENT }}>{initial?.id ? "Edit" : "New"} {role === "recruiter" ? "Recruiter" : "Setter"} Agreement</p>
            <p className="font-head font-black text-white text-sm mt-0.5">Fill in details to generate agreement</p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-4 overflow-y-auto max-h-[70vh]">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><label className={labelCls} style={{ color: "rgba(255,255,255,0.4)" }}>Full Name *</label><input data-testid="staff-name" value={form.name} onChange={set("name")} placeholder="e.g. John Smith" className={inputCls} style={inputStyle} /></div>
            <div><label className={labelCls} style={{ color: "rgba(255,255,255,0.4)" }}>Email</label><input type="email" value={form.email} onChange={set("email")} placeholder="john@email.com" className={inputCls} style={inputStyle} /></div>
            <div><label className={labelCls} style={{ color: "rgba(255,255,255,0.4)" }}>Phone</label><input value={form.phone} onChange={set("phone")} placeholder="+1 (000) 000-0000" className={inputCls} style={inputStyle} /></div>
            <div><label className={labelCls} style={{ color: "rgba(255,255,255,0.4)" }}>Start Date</label><input type="date" value={form.start_date} onChange={set("start_date")} className={inputCls} style={inputStyle} /></div>
            <div><label className={labelCls} style={{ color: "rgba(255,255,255,0.4)" }}>Custom Notes / Rate Override <span style={{ opacity: 0.4, fontSize: "9px" }}>(optional)</span></label><input value={form.commission} onChange={set("commission")} placeholder={role === "recruiter" ? "e.g. Negotiated $350 flat — see note" : "e.g. Agreed $120 per qualified call"} className={inputCls} style={inputStyle} /></div>
            <div><label className={labelCls} style={{ color: "rgba(255,255,255,0.4)" }}>Signature Date</label><input type="date" value={form.signed_at} onChange={set("signed_at")} className={inputCls} style={inputStyle} /></div>
            <div><label className={labelCls} style={{ color: "rgba(255,255,255,0.4)" }}>Signer Name (for signature block)</label><input value={form.signer_name} onChange={set("signer_name")} placeholder="Printed name" className={inputCls} style={inputStyle} /></div>
            <div className="col-span-2"><label className={labelCls} style={{ color: "rgba(255,255,255,0.4)" }}>Notes (internal)</label><input value={form.notes} onChange={set("notes")} placeholder="Optional" className={inputCls} style={inputStyle} /></div>
          </div>
        </div>
        <div className="px-6 py-4 flex justify-end gap-3" style={{ borderTop: "1px solid rgba(255,255,255,0.07)", background: "#0d0d0d" }}>
          <button onClick={onClose} className="font-head text-xs uppercase tracking-[0.2em] px-5 py-2.5 border text-white/40 hover:text-white/70 transition-colors" style={{ borderColor: "rgba(255,255,255,0.1)" }}>Cancel</button>
          <button data-testid="staff-save-btn" onClick={save} disabled={saving || !form.name.trim()} className="font-head text-xs font-black uppercase tracking-[0.2em] px-6 py-2.5 transition-all disabled:opacity-30" style={{ background: ACCENT, color: "#fff" }}>
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// ─── Action buttons row ───────────────────────────────────────────────────
function AgreementActions({ onPreview, onPrint }) {
  return (
    <div className="flex items-center gap-2">
      <button onClick={onPreview} data-testid="preview-agreement-btn"
        className="flex items-center gap-1.5 font-head text-[10px] uppercase tracking-[0.12em] px-3 py-1.5 transition-all hover:brightness-110"
        style={{ background: "rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.55)", border: "1px solid rgba(255,255,255,0.1)" }}>
        <Eye className="w-3 h-3" /> Preview
      </button>
      <button onClick={onPrint} data-testid="print-agreement-btn"
        className="flex items-center gap-1.5 font-head text-[10px] uppercase tracking-[0.12em] px-3 py-1.5 transition-all hover:brightness-110"
        style={{ background: "rgba(255,77,0,0.08)", color: ACCENT, border: `1px solid rgba(255,77,0,0.2)` }}>
        <Printer className="w-3 h-3" /> Print
      </button>
    </div>
  );
}

// ─── Main Panel ───────────────────────────────────────────────────────────
const TABS = [
  { key: "carriers",   label: "Carriers",   icon: Building2 },
  { key: "drivers",    label: "Drivers",    icon: User },
  { key: "recruiters", label: "Recruiters", icon: Users },
  { key: "setters",    label: "Setters",    icon: Briefcase },
];

export function AgreementsPanel({ token }) {
  const [tab,       setTab]       = useState("carriers");
  const [carriers,  setCarriers]  = useState([]);
  const [drivers,   setDrivers]   = useState([]);
  const [staff,     setStaff]     = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [search,    setSearch]    = useState("");
  const [editModal, setEditModal] = useState(null);
  const [preview,   setPreview]   = useState(null); // { type, item }
  const [confirmRemove, setConfirmRemove] = useState(null); // { type, id, label }

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ca, da, sa] = await Promise.all([
        api.marketAdminCarrierAgreements(token),
        api.marketAdminDriverAgreements(token),
        api.marketAdminStaffAgreements(token),
      ]);
      setCarriers(ca.agreements || []);
      setDrivers(da.agreements || []);
      setStaff(sa.agreements || []);
    } catch { toast.error("Failed to load agreements"); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const recruiters = staff.filter(s => s.role === "recruiter");
  const setters    = staff.filter(s => s.role === "setter");

  const filterFn = (arr, keys) => arr.filter(r => !search || keys.some(k => (r[k] || "").toLowerCase().includes(search.toLowerCase())));
  const fc = filterFn(carriers,   ["carrier_name", "signer_name", "email"]);
  const fd = filterFn(drivers,    ["first_name", "last_name", "email", "state", "cdl_class"]);
  const fr = filterFn(recruiters, ["name", "email"]);
  const fs = filterFn(setters,    ["name", "email"]);

  async function handleDeleteStaff(id) {
    try { await api.marketAdminDeleteStaffAgreement(token, id); setStaff(p => p.filter(s => s.id !== id)); toast.success("Archived"); }
    catch { toast.error("Failed"); }
  }

  async function handleRemove(type, id) {
    try {
      if (type === "carrier") {
        await api.marketAdminRevokeCarrierAgreement(token, id);
        setCarriers(p => p.filter(c => c.phone !== id));
        toast.success("Carrier agreement revoked");
      } else if (type === "driver") {
        await api.marketAdminArchiveDriverAgreement(token, id);
        setDrivers(p => p.filter(d => d.id !== id));
        toast.success("Driver agreement archived");
      }
    } catch { toast.error("Remove failed"); }
    finally { setConfirmRemove(null); }
  }

  async function handleToggleVisibility(phone, currentlyShown) {
    const newShow = !currentlyShown;
    try {
      await api.marketAdminToggleCarrierVisibility(token, phone, newShow);
      setCarriers(p => p.map(c => c.phone === phone ? { ...c, show_on_partners_page: newShow } : c));
      toast.success(newShow ? "Carrier shown on Partners page" : "Carrier hidden from Partners page");
    } catch { toast.error("Failed to update visibility"); }
  }

  function handleStaffSaved(saved) {
    setStaff(prev => { const i = prev.findIndex(s => s.id === saved.id); if (i >= 0) { const n = [...prev]; n[i] = saved; return n; } return [saved, ...prev]; });
    setEditModal(null);
  }

  const tdCls = "px-4 py-3 text-sm";
  const thCls = "px-4 py-2.5 font-head text-[9px] uppercase tracking-[0.15em] text-left";
  const rowHover = { background: "rgba(255,255,255,0.025)" };
  const emptyRow = msg => <tr><td colSpan={10} className="px-4 py-12 text-center text-white/25 font-head text-xs uppercase tracking-[0.2em]">{msg}</td></tr>;
  const count = { carriers: fc.length, drivers: fd.length, recruiters: fr.length, setters: fs.length };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-head text-[9px] uppercase tracking-[0.25em] mb-1" style={{ color: ACCENT }}>Admin Hub</p>
          <h2 className="font-head font-black text-white text-xl tracking-tight">Agreements Archive</h2>
          <p className="text-white/35 text-xs mt-1">All signed agreements — carriers, drivers, recruiters, setters.</p>
        </div>
        <button onClick={fetchAll} className="flex items-center gap-2 font-head text-[10px] uppercase tracking-[0.15em] px-3 py-2 text-white/40 hover:text-white/70 transition-colors" style={{ border: "1px solid rgba(255,255,255,0.08)" }}>
          <Loader2 className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-0" style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} data-testid={`agreements-sub-tab-${key}`} onClick={() => setTab(key)}
            className="flex items-center gap-2 px-5 py-3 font-head text-[10px] uppercase tracking-[0.18em] transition-all relative"
            style={{ color: tab === key ? "white" : "rgba(255,255,255,0.35)", background: tab === key ? "rgba(255,255,255,0.03)" : "transparent" }}>
            <Icon className="w-3.5 h-3.5" />
            {label}
            <span className="font-head text-[9px] px-1.5 py-0.5 ml-0.5" style={{ background: tab === key ? "rgba(255,77,0,0.15)" : "rgba(255,255,255,0.05)", color: tab === key ? ACCENT : "rgba(255,255,255,0.25)" }}>{count[key]}</span>
            {tab === key && <motion.div layoutId="agr-tab-line" className="absolute bottom-0 left-0 right-0 h-0.5" style={{ background: ACCENT }} />}
          </button>
        ))}
      </div>

      {/* Search + action */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…" className="w-full pl-9 pr-4 py-2 text-sm text-white outline-none font-head" style={{ background: "#0d0d0d", border: "1px solid rgba(255,255,255,0.08)" }} />
        </div>
        {(tab === "recruiters" || tab === "setters") && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPreview({ type: tab === "recruiters" ? "recruiter" : "setter", item: null })}
              data-testid="blank-preview-btn"
              className="flex items-center gap-2 font-head text-[10px] uppercase tracking-[0.15em] px-4 py-2 transition-all hover:brightness-110"
              style={{ background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.5)", border: "1px solid rgba(255,255,255,0.12)" }}>
              <Eye className="w-3.5 h-3.5" /> Blank Preview
            </button>
            <button
              onClick={() => { const url = `${window.location.origin}/agreement-preview/${tab === "recruiters" ? "recruiter" : "setter"}`; navigator.clipboard.writeText(url); toast.success("Preview link copied!"); }}
              data-testid={`share-link-${tab}`}
              className="flex items-center gap-2 font-head text-[10px] uppercase tracking-[0.15em] px-4 py-2 transition-all hover:brightness-110"
              style={{ background: "rgba(245,200,66,0.08)", color: "#F5C842", border: "1px solid rgba(245,200,66,0.2)" }}>
              <Link2 className="w-3.5 h-3.5" /> Share Link
            </button>
            <button onClick={() => setEditModal({ item: null, role: tab === "recruiters" ? "recruiter" : "setter" })} data-testid="new-staff-agreement-btn"
            className="flex items-center gap-2 font-head text-[10px] font-black uppercase tracking-[0.18em] px-4 py-2 transition-all hover:brightness-110" style={{ background: ACCENT, color: "#fff" }}>
            <Plus className="w-3.5 h-3.5" /> New Agreement
          </button>
          </div>
        )}
        {tab === "carriers" && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPreview({ type: "fleet", item: {} })}
              data-testid="fleet-template-preview-btn"
              className="flex items-center gap-2 font-head text-[10px] uppercase tracking-[0.15em] px-4 py-2 transition-all hover:brightness-110"
              style={{ background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.5)", border: "1px solid rgba(255,255,255,0.12)" }}>
              <Eye className="w-3.5 h-3.5" /> Fleet Template
            </button>
            <button
              onClick={() => { const url = `${window.location.origin}/agreement-preview/fleet`; navigator.clipboard.writeText(url); toast.success("Fleet preview link copied!"); }}
              data-testid="share-link-fleet"
              className="flex items-center gap-2 font-head text-[10px] uppercase tracking-[0.15em] px-4 py-2 transition-all hover:brightness-110"
              style={{ background: "rgba(245,200,66,0.08)", color: "#F5C842", border: "1px solid rgba(245,200,66,0.2)" }}>
              <Link2 className="w-3.5 h-3.5" /> Share Link
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-white/30" /></div>
      ) : (
        <div className="overflow-x-auto" style={{ border: "1px solid rgba(255,255,255,0.06)" }}>
          <table className="w-full border-collapse">
            <thead style={{ background: "#0d0d0d", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
              {tab === "carriers"   && <tr>{["Company","Signer","Email","Signed Date","Visible",""].map(h => <th key={h} className={thCls} style={{ color: "rgba(255,255,255,0.3)" }}>{h}</th>)}</tr>}
              {tab === "drivers"    && <tr>{["Driver","CDL","State","Position","Applied","Status",""].map(h => <th key={h} className={thCls} style={{ color: "rgba(255,255,255,0.3)" }}>{h}</th>)}</tr>}              {(tab === "recruiters" || tab === "setters") && <tr>{["Name","Email","Start Date",tab === "recruiters" ? "Commission" : "Rate","Signed",""].map(h => <th key={h} className={thCls} style={{ color: "rgba(255,255,255,0.3)" }}>{h}</th>)}</tr>}
            </thead>
            <tbody style={{ color: "rgba(255,255,255,0.7)" }}>

              {tab === "carriers" && (fc.length === 0 ? emptyRow("No signed carrier agreements yet") : fc.map((a, i) => (
                <tr key={i} className="transition-colors" style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }} onMouseEnter={e => Object.assign(e.currentTarget.style, rowHover)} onMouseLeave={e => e.currentTarget.style.background = ""}>
                  <td className={tdCls}><span className="font-head font-black text-white">{a.carrier_name}</span></td>
                  <td className={tdCls} style={{ fontStyle: "italic", color: GOLD }}>{a.signer_name}</td>
                  <td className={tdCls} style={{ color: "rgba(255,255,255,0.4)" }}>{a.email}</td>
                  <td className={tdCls} style={{ color: "rgba(255,255,255,0.5)" }}>{fmtDate(a.signed_at)}</td>
                  <td className="px-4 py-3">
                    <button
                      data-testid={`toggle-partner-visibility-${i}`}
                      onClick={() => handleToggleVisibility(a.phone, a.show_on_partners_page !== false)}
                      title={a.show_on_partners_page !== false ? "Hide from Partners page" : "Show on Partners page"}
                      className="flex items-center gap-1.5 font-head text-[10px] uppercase tracking-[0.12em] px-3 py-1.5 transition-all hover:brightness-110"
                      style={{
                        background: a.show_on_partners_page !== false ? "rgba(255,77,0,0.08)" : "rgba(255,255,255,0.04)",
                        color: a.show_on_partners_page !== false ? ACCENT : "rgba(255,255,255,0.3)",
                        border: `1px solid ${a.show_on_partners_page !== false ? "rgba(255,77,0,0.25)" : "rgba(255,255,255,0.1)"}`,
                      }}>
                      {a.show_on_partners_page !== false ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                      {a.show_on_partners_page !== false ? "Visible" : "Hidden"}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <AgreementActions onPreview={() => setPreview({ type: "carrier", item: a })} onPrint={() => printAgreement("carrier", a)} />
                      <button onClick={() => setConfirmRemove({ type: "carrier", id: a.phone, label: a.carrier_name })} data-testid={`remove-carrier-agr-${i}`}
                        className="flex items-center gap-1.5 font-head text-[10px] uppercase tracking-[0.12em] px-3 py-1.5 transition-all hover:brightness-110"
                        style={{ background: "rgba(239,68,68,0.08)", color: "#EF4444", border: "1px solid rgba(239,68,68,0.2)" }}>
                        <Trash2 className="w-3 h-3" /> Remove
                      </button>
                    </div>
                  </td>
                </tr>
              )))}

              {tab === "drivers" && (fd.length === 0 ? emptyRow("No driver applications yet") : fd.map((d, i) => (
                <tr key={i} className="transition-colors" style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }} onMouseEnter={e => Object.assign(e.currentTarget.style, rowHover)} onMouseLeave={e => e.currentTarget.style.background = ""}>
                  <td className={tdCls}><span className="font-head font-black text-white">{d.first_name} {d.last_name}</span><p className="text-xs" style={{ color: "rgba(255,255,255,0.3)" }}>{d.email}</p></td>
                  <td className={tdCls}><span className="font-head font-black" style={{ color: GOLD }}>{d.cdl_class}</span></td>
                  <td className={tdCls} style={{ color: "rgba(255,255,255,0.5)" }}>{d.state}</td>
                  <td className={tdCls} style={{ color: "rgba(255,255,255,0.5)", fontSize: "11px" }}>{d.position}</td>
                  <td className={tdCls} style={{ color: "rgba(255,255,255,0.4)" }}>{fmtDate(d.created_at)}</td>
                  <td className="px-4 py-3"><StatusBadge status={d.market_status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <AgreementActions onPreview={() => setPreview({ type: "driver", item: d })} onPrint={() => printAgreement("driver", d)} />
                      <button onClick={() => setConfirmRemove({ type: "driver", id: d.id, label: `${d.first_name} ${d.last_name}` })} data-testid={`remove-driver-agr-${i}`}
                        className="flex items-center gap-1.5 font-head text-[10px] uppercase tracking-[0.12em] px-3 py-1.5 transition-all hover:brightness-110"
                        style={{ background: "rgba(239,68,68,0.08)", color: "#EF4444", border: "1px solid rgba(239,68,68,0.2)" }}>
                        <Trash2 className="w-3 h-3" /> Remove
                      </button>
                    </div>
                  </td>
                </tr>
              )))}

              {tab === "recruiters" && (fr.length === 0 ? emptyRow("No recruiter agreements — click 'New Agreement'") : fr.map((r, i) => (
                <tr key={i} className="transition-colors" style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }} onMouseEnter={e => Object.assign(e.currentTarget.style, rowHover)} onMouseLeave={e => e.currentTarget.style.background = ""}>
                  <td className={tdCls}><span className="font-head font-black text-white">{r.name || "—"}</span></td>
                  <td className={tdCls} style={{ color: "rgba(255,255,255,0.4)" }}>{r.email || "—"}</td>
                  <td className={tdCls} style={{ color: "rgba(255,255,255,0.5)" }}>{r.start_date ? fmtDate(r.start_date) : "—"}</td>
                  <td className={tdCls} style={{ color: GOLD }}>{r.commission || "—"}</td>
                  <td className={tdCls} style={{ color: "rgba(255,255,255,0.4)" }}>{r.signed_at ? fmtDate(r.signed_at) : <span style={{ color: "rgba(255,77,0,0.6)" }}>Unsigned</span>}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => setEditModal({ item: r, role: "recruiter" })} className="flex items-center gap-1.5 font-head text-[10px] uppercase tracking-[0.12em] px-3 py-1.5 transition-all hover:brightness-110" style={{ background: "rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.5)", border: "1px solid rgba(255,255,255,0.1)" }}><Pencil className="w-3 h-3" /> Edit</button>
                      <AgreementActions onPreview={() => setPreview({ type: "recruiter", item: r })} onPrint={() => printAgreement("recruiter", r)} />
                      <button onClick={() => setConfirmRemove({ type: "staff", id: r.id, label: r.name || "Recruiter" })} data-testid={`remove-recruiter-agr-${i}`}
                        className="flex items-center gap-1.5 font-head text-[10px] uppercase tracking-[0.12em] px-3 py-1.5 transition-all hover:brightness-110"
                        style={{ background: "rgba(239,68,68,0.08)", color: "#EF4444", border: "1px solid rgba(239,68,68,0.2)" }}>
                        <Trash2 className="w-3 h-3" /> Remove
                      </button>
                    </div>
                  </td>
                </tr>
              )))}

              {tab === "setters" && (fs.length === 0 ? emptyRow("No setter agreements — click 'New Agreement'") : fs.map((r, i) => (
                <tr key={i} className="transition-colors" style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }} onMouseEnter={e => Object.assign(e.currentTarget.style, rowHover)} onMouseLeave={e => e.currentTarget.style.background = ""}>
                  <td className={tdCls}><span className="font-head font-black text-white">{r.name || "—"}</span></td>
                  <td className={tdCls} style={{ color: "rgba(255,255,255,0.4)" }}>{r.email || "—"}</td>
                  <td className={tdCls} style={{ color: "rgba(255,255,255,0.5)" }}>{r.start_date ? fmtDate(r.start_date) : "—"}</td>
                  <td className={tdCls} style={{ color: GOLD }}>{r.commission || "—"}</td>
                  <td className={tdCls} style={{ color: "rgba(255,255,255,0.4)" }}>{r.signed_at ? fmtDate(r.signed_at) : <span style={{ color: "rgba(255,77,0,0.6)" }}>Unsigned</span>}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => setEditModal({ item: r, role: "setter" })} className="flex items-center gap-1.5 font-head text-[10px] uppercase tracking-[0.12em] px-3 py-1.5 transition-all hover:brightness-110" style={{ background: "rgba(255,255,255,0.04)", color: "rgba(255,255,255,0.5)", border: "1px solid rgba(255,255,255,0.1)" }}><Pencil className="w-3 h-3" /> Edit</button>
                      <AgreementActions onPreview={() => setPreview({ type: "setter", item: r })} onPrint={() => printAgreement("setter", r)} />
                      <button onClick={() => setConfirmRemove({ type: "staff", id: r.id, label: r.name || "Setter" })} data-testid={`remove-setter-agr-${i}`}
                        className="flex items-center gap-1.5 font-head text-[10px] uppercase tracking-[0.12em] px-3 py-1.5 transition-all hover:brightness-110"
                        style={{ background: "rgba(239,68,68,0.08)", color: "#EF4444", border: "1px solid rgba(239,68,68,0.2)" }}>
                        <Trash2 className="w-3 h-3" /> Remove
                      </button>
                    </div>
                  </td>
                </tr>
              )))}

            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      <AnimatePresence>
        {editModal && <StaffEditModal key="edit" token={token} initial={editModal.item} role={editModal.role} onSaved={handleStaffSaved} onClose={() => setEditModal(null)} />}
        {preview   && <PreviewModal  key="preview" type={preview.type} item={preview.item} token={token} onClose={() => setPreview(null)} />}
        {confirmRemove && (
          <motion.div key="confirm-remove" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.88)" }}>
            <motion.div initial={{ scale: 0.95, y: 10 }} animate={{ scale: 1, y: 0 }}
              className="w-full max-w-sm rounded-sm overflow-hidden" style={{ background: CARD, border: "1px solid rgba(239,68,68,0.3)" }}>
              <div className="px-6 py-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 flex items-center justify-center" style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)" }}>
                    <Trash2 className="w-4 h-4" style={{ color: "#EF4444" }} />
                  </div>
                  <div>
                    <p className="font-head text-[9px] uppercase tracking-[0.2em]" style={{ color: "#EF4444" }}>Confirm Remove</p>
                    <p className="font-head font-black text-white text-sm mt-0.5">
                      {confirmRemove.type === "carrier" ? "Revoke carrier agreement?" : "Archive this agreement?"}
                    </p>
                  </div>
                </div>
                <p className="text-white/50 text-xs mb-5">
                  <strong className="text-white">{confirmRemove.label}</strong>
                  {confirmRemove.type === "carrier"
                    ? " will need to re-sign the Fleet Client Agreement to place bids."
                    : " will be removed from this list (soft-delete, data is preserved)."}
                </p>
                <div className="flex gap-3">
                  <button onClick={() => setConfirmRemove(null)}
                    className="flex-1 font-head text-xs uppercase tracking-[0.2em] py-2.5 border text-white/40 hover:text-white/70 transition-colors"
                    style={{ borderColor: "rgba(255,255,255,0.1)" }}>
                    Cancel
                  </button>
                  <button data-testid="confirm-remove-btn"
                    onClick={() => confirmRemove.type === "staff" ? (handleDeleteStaff(confirmRemove.id), setConfirmRemove(null)) : handleRemove(confirmRemove.type, confirmRemove.id)}
                    className="flex-1 font-head text-xs font-black uppercase tracking-[0.2em] py-2.5 transition-all hover:brightness-110"
                    style={{ background: "#EF4444", color: "#fff" }}>
                    Remove
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
